// studioMode — the classic canvas's "Canvas | Editor" switch (PS-57), kept
// for the old Studio at `/classic/...` (#315): "Editor" opens the Studio's
// pages (the three columns replaced the Editor mode) where the author last
// was, else at the one selected card, else at the first course or module.
// Pure.

export type { StudioMode };
export { editorEntry, placeResolves };

import { exhaustSwitchCase } from "@bilda/utils";
import type { StudioCard } from "../Studio/studioCards.js";
import {
    type DeskCommand,
    type DeskState,
    deskBinder,
    deskDocument,
    inFocus,
    openCommands,
} from "../Studio/studioDesk.js";

type StudioMode = "canvas" | "editor";

// The level shown is still in the project (a container or a document).
const placeResolves = (
    desk: DeskState,
    cards: readonly StudioCard[],
): boolean => {
    const shown = inFocus(desk);
    if (shown === undefined) return false;
    switch (shown.kind) {
        case "binder":
        case "shelf":
            return deskBinder(cards, shown.id) !== undefined;
        case "document":
            return deskDocument(cards, shown.id) !== undefined;
        case "info":
        case "objectives":
        case "sources":
            return true;
        default:
            return exhaustSwitchCase(shown.kind);
    }
};

// Into the Editor by the switch: where the author was (no commands), else
// the one selected card, else the first container, else nothing to open.
const editorEntry = (
    desk: DeskState,
    cards: readonly StudioCard[],
    selected: readonly string[],
): readonly DeskCommand[] => {
    if (placeResolves(desk, cards)) return [];
    const [only, ...more] = selected;
    const picked =
        more.length === 0 ? cards.find((c) => c.id === only) : undefined;
    const first = picked ?? cards.find((c) => c.type === "container");
    return first === undefined ? [] : openCommands(first, undefined);
};
